import TemplateBackground from './TemplateBackground';

/**
 * 模板缩略示意图。
 * 用纯 CSS 拼出各模板的布局特征，避免依赖图片素材。
 * 背景层会渲染该模板的真实背景 SVG（淡化处理），便于在后台/选择器中直观预览。
 */
export default function TemplateThumb({
  slug,
  layout,
  bgSvg,
  className = '',
}: {
  slug: string;
  /** 实际布局 key（自定义模板会指向某个内置布局）；为空时等同 slug */
  layout?: string | null;
  bgSvg?: string | null;
  className?: string;
}) {
  const box = 'rounded bg-current';
  const key = layout || slug;

  return (
    <div
      className={`relative isolate flex h-20 w-full overflow-hidden rounded-lg border border-ink-200 bg-ink-50 p-2 ${className}`}
      aria-hidden
    >
      <TemplateBackground slug={key} bgSvg={bgSvg} className="opacity-25" />
      {key === 'classic' && (
        <div className="flex w-full flex-col items-center gap-1.5">
          <div className={`${box} h-2 w-10 opacity-70`} />
          <div className={`${box} h-1.5 w-16 opacity-40`} />
          <div className="mt-1 grid w-full grid-cols-5 gap-1">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className={`${box} h-3 opacity-25`} />
            ))}
          </div>
        </div>
      )}

      {key === 'chinese' && (
        <div className="flex w-full items-center justify-center gap-2">
          <div className={`${box} h-12 w-1.5 opacity-70`} />
          <div className={`${box} h-1.5 w-8 opacity-40`} />
          <div className="flex flex-1 flex-col items-center gap-1">
            <div className={`${box} h-8 w-full opacity-25`} />
            <div className={`${box} h-1 w-6 opacity-30`} />
          </div>
        </div>
      )}

      {key === 'vigil' && (
        <div className="relative flex w-full items-center justify-center bg-ink-900">
          <div className="h-6 w-6 rounded-full bg-candle-400/70" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
            <div
              key={deg}
              className="absolute h-1.5 w-1.5 rounded-full bg-candle-400/60"
              style={{
                transform: `rotate(${deg}deg) translateY(-22px)`,
              }}
            />
          ))}
        </div>
      )}

      {key === 'cards' && (
        <div className="flex w-full flex-col gap-1.5">
          <div className={`${box} h-5 w-full opacity-30`} />
          <div className={`${box} h-1.5 w-12 opacity-70`} />
          <div className="grid grid-cols-2 gap-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`${box} h-4 opacity-25`} />
            ))}
          </div>
        </div>
      )}

      {key === 'minimal' && (
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <div className={`${box} h-1.5 w-14 opacity-50`} />
          <div className={`${box} h-1 w-10 opacity-25`} />
          <div className="mt-2 flex gap-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-1.5 w-1.5 rounded-full bg-current opacity-30" />
            ))}
          </div>
        </div>
      )}

      {key === 'lingtang-hall' && (
        <div className="flex w-full items-center justify-center gap-1.5">
          <div className={`${box} h-10 w-1 opacity-50`} />
          <div className={`${box} h-9 w-6 opacity-70`} />
          <div className={`${box} h-1.5 w-6 opacity-40`} />
          <div className={`${box} h-9 w-6 opacity-70`} />
          <div className={`${box} h-10 w-1 opacity-50`} />
        </div>
      )}

      {key === 'lingtang-night' && (
        <div className="relative flex w-full items-center justify-center bg-ink-900">
          <div className="h-7 w-7 rounded-full bg-candle-400/70" />
          {[0, 90, 180, 270].map((deg) => (
            <div
              key={deg}
              className="absolute h-1.5 w-1.5 rounded-full bg-candle-400/60"
              style={{
                transform: `rotate(${deg}deg) translateY(-20px)`,
              }}
            />
          ))}
        </div>
      )}

      {key === 'lingtang-wreath' && (
        <div className="flex w-full items-center justify-center">
          <div className="relative h-12 w-12 rounded-full border-4 border-current opacity-30">
            <div className={`${box} absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 opacity-70`} />
          </div>
        </div>
      )}
    </div>
  );
}
